<template>
  <div class="readonly-formcolor">
    <div class="breadcrumbs mb-2">
      <v-breadcrumbs divider=">" :items="breadcrumbsList">
        <template v-slot:item="{ item }">
          <v-breadcrumbs-item
            :to="{ name: item.path }"
            :disabled="item.disabled"
            exact
          >
            {{ item.text }}
          </v-breadcrumbs-item>
        </template>
      </v-breadcrumbs>
    </div>
    <PageTitle :pageHeading="pageHeading" :rightsideButton="false"> </PageTitle>
    <v-container class="create-service-hours">
      <v-form ref="servicehours" v-model="formValid">
        <div class="customize-select">
          <v-row no-gutters>
            <v-col>
              <v-text-field
                v-model="name"
                outlined
                required
                class="required"
                label="Name"
                :rules="[(v) => !!v || 'Name is required']"
                :disabled="readOnly"
              >
              </v-text-field>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col>
              <v-textarea
                outlined
                label="Description"
                auto-grow
                v-model="description"
                :disabled="readOnly"
              ></v-textarea>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col>
              <v-autocomplete
                v-model="selectedTimeZone"
                :items="timeZoneList"
                label="Time Zone"
                class="required"
                outlined
                required
                item-text="identifier"
                item-value="identifier"
                :disabled="readOnly"
                :rules="[(v) => !!v || 'Time Zone is required']"
              >
              </v-autocomplete>
            </v-col>
          </v-row>
        </div>
        <v-row class="mt-5">
          <v-col cols="12" md="6">
            <v-card outlined elevation="1" class="setservice-hours">
              <v-card-title
                class="secondary--font font-weight-medium-custom ml-2"
              >
                Set Service Hours
              </v-card-title>
              <v-card-text>
                <v-radio-group v-model="customHours">
                  <v-row class="pl-2">
                    <v-col cols="6">
                      <v-radio
                        label="Round the clock"
                        value="24hours"
                        color="#03B6FC"
                        :disabled="readOnly"
                      >
                      </v-radio>
                      <div class="body-2 grey--text text--darken-2 pl-7">
                        (24 hours a day, 7 days a week)
                      </div>
                    </v-col>
                    <v-col cols="6">
                      <v-radio
                        label="Custom service hours"
                        value="custom"
                        :disabled="readOnly"
                        color="#03B6FC"
                        @click="setDefaultHours('custom')"
                      ></v-radio>
                      <div class="body-2 grey--text text--darken-2 pl-7">
                        (Set up custom working hours)
                      </div>
                    </v-col>
                  </v-row>
                </v-radio-group>
                <div v-if="customHours == 'custom'">
                  <v-row class="mt-5 ml-7 mr-2">
                    <v-col cols="12" class="">
                      <p class="body-1 font-weight-medium-custom">
                        <span class="secondary--font pl-1 black--text">
                          Select working days and enter hours below
                        </span>
                      </p></v-col
                    >
                  </v-row>
                  <v-row
                    class="ml-7 mr-2 mt-0"
                    v-for="(day, name, index) in customServiceHours"
                    :key="index"
                  >
                    <p
                      class="error--text mb-n3 ml-5 width-100"
                      v-if="day.error"
                    >
                      Start time should be earlier than end time
                    </p>
                    <v-col cols="2">
                      <v-checkbox
                        dense
                        v-model="day.active"
                        :label="name.slice(0, 3)"
                        class="text-capitalize"
                        color="#03B6FC"
                        @change="setDefaultValue(name)"
                        :disabled="readOnly"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="4">
                      <v-combobox
                        v-model="day.start_time"
                        class="pr-2"
                        dense
                        item-color="#03B6FC"
                        :items="day.startHours"
                        placeholder="AM"
                        :disabled="day.active !== true || readOnly"
                        @change="
                          changeEndTime(name, day.start_time, day.end_time)
                        "
                      />
                    </v-col>
                    <v-col cols="1" class="pt-5 pl-2 body-1"> to </v-col>
                    <v-col cols="4">
                      <v-combobox
                        v-model="day.end_time"
                        class="pr-2"
                        dense
                        item-color="#03B6FC"
                        :items="day.endHours"
                        placeholder="PM"
                        :disabled="day.active !== true || readOnly"
                        @change="
                          checkTimeValidation(
                            name,
                            day.start_time,
                            day.end_time
                          )
                        "
                      />
                    </v-col>
                  </v-row>
                </div>
                <p class="error--text ml-10" v-show="showError">
                  Please select atleast one working day
                </p>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <HoliDaysManagement
              @update="getHolidays"
              :holidays="holidayList"
              :readOnly="readOnly"
              :key="loading"
            >
            </HoliDaysManagement>
          </v-col>
        </v-row>
        <v-card
          v-if="readOnly && associatedGroups.length"
          color="#F2F2F2"
          class="d-flex pa-4 mt-8"
          elevation="0"
        >
          <div class="body-1">
            <span class="font-weight-medium-custom secondary--font"
              >Associated Groups :
            </span>
          </div>
          <div class="body-1 ml-2">
            <template v-for="(group, index) in associatedGroups">
              <span :key="group">
                <span v-if="index !== 0">, </span> {{ group | upperCaseFirst }}
              </span>
            </template>
          </div>
        </v-card>

        <v-divider class="mt-12 mb-6"></v-divider>
        <v-row class="mt-5">
          <v-col cols="auto">
            <v-btn
              v-if="readOnly"
              outlined
              @click="redirectBack()"
              class="font-weight-bold"
            >
              Back
            </v-btn>

            <v-btn
              v-else
              outlined
              @click="cancelServiceHours()"
              class="font-weight-bold"
            >
              Cancel
            </v-btn>
          </v-col>
          <v-col cols="auto" v-if="readOnly">
            <v-btn dark class="font-weight-bold" @click="enableEdit()">
              edit
            </v-btn>
          </v-col>

          <v-col cols="auto" v-else>
            <v-btn
              v-if="editAction"
              :dark="formValid && hoursValidation"
              :disabled="!formValid || hoursValidation !== true"
              @click="editServiceHours()"
              class="font-weight-bold"
            >
              Save
            </v-btn>
            <v-btn
              v-else
              :dark="formValid && hoursValidation"
              :disabled="!formValid || hoursValidation !== true"
              @click="createServiceHour()"
              class="font-weight-bold"
            >
              Save
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-container>
    <v-overlay :value="loading">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import PageTitle from "@/components/shared/PageTitle.vue";
import HoliDaysManagement from "@/components/settings/service_hours/HolidaysManagement.vue";
import { $servicehours } from "@/services/Index";
import { mapGetters, mapActions } from "vuex";

export default {
  components: {
    PageTitle,
    HoliDaysManagement,
  },
  data: () => {
    return {
      breadcrumbsList: [
        {
          text: "Settings",
          disabled: false,
          path: "settings",
        },
        {
          text: "All Service hours",
          disabled: false,
          path: "service-hours",
        },
        {
          text: "New service hour",
          disabled: true,
          path: "",
        },
      ],
      pageHeading: "New Service Hour",
      formValid: false,
      name: null,
      description: null,
      selectedTimeZone: null,
      customHours: "24hours",
      customServiceHours: {
        sunday: {
          start_time: null,
          active: false,
          end_time: null,
          error: false,
          startHours: [],
          endHours: [],
        },
        monday: {
          start_time: null,
          active: false,
          end_time: null,
          error: false,
          startHours: [],
          endHours: [],
        },
        tuesday: {
          start_time: null,
          active: false,
          end_time: null,
          error: false,
          startHours: [],
          endHours: [],
        },
        wednesday: {
          start_time: null,
          active: false,
          end_time: null,
          error: false,
          startHours: [],
          endHours: [],
        },
        thursday: {
          start_time: null,
          active: false,
          end_time: null,
          error: false,
          startHours: [],
          endHours: [],
        },
        friday: {
          start_time: null,
          active: false,
          end_time: null,
          error: false,
          startHours: [],
          endHours: [],
        },
        saturday: {
          start_time: null,
          active: false,
          end_time: null,
          error: false,
          startHours: [],
          endHours: [],
        },
      },
      holidayList: [],
      readOnly: false,
      loading: false,
      editAction: false,
      timings: {},
      showError: false,
      associatedGroups: [],
      startEndTimeError: false,
    };
  },
  computed: {
    /** getting timezone list from store */
    ...mapGetters({
      timeZoneList: ["app/timezones"],
    }),
    hoursValidation() {
      return this.validateHours();
    },
  },

  mounted() {
    this.readQueryParams();
    let timeList = this.timeList();
    Object.keys(this.customServiceHours).forEach((time) => {
      this.customServiceHours[time].startHours = timeList;
      this.customServiceHours[time].endHours = timeList;
    });

    /** get timezones */
    this.getTimeZones();
  },

  methods: {
    ...mapActions({
      getTimeZones: "app/getTimezones",
    }),
    setDefaultHours(value) {
      if (value == "custom") {
        for (const item in this.customServiceHours) {
          if (item !== "sunday" && item !== "saturday") {
            this.customServiceHours[item].active = true;
            this.customServiceHours[item].start_time = "09:00 AM";
            this.customServiceHours[item].end_time = "05:00 PM";
            this.changeEndTime(
              item,
              this.customServiceHours[item].start_time,
              this.customServiceHours[item].end_time
            );
          }
        }
      }
    },
    readQueryParams() {
      if (this.$route.params.id) {
        this.getServiceHour(this.$route.params.id);
        this.readOnly = true;
      } else {
        this.setDefaultTimeZone();
      }
      if (this.$route.query.edit == "true") {
        this.enableEdit();
      }
    },
    enableEdit() {
      this.editAction = true;
      this.readOnly = false;
    },

    setDefaultTimeZone() {
      if (
        Intl.DateTimeFormat().resolvedOptions().timeZone === "Asia/Calcutta"
      ) {
        this.selectedTimeZone = "Asia/Kolkata";
      } else {
        this.selectedTimeZone =
          Intl.DateTimeFormat().resolvedOptions().timeZone;
      }
    },

    getHolidays(data) {
      this.holidayList = data;
    },

    cancelServiceHours() {
      if (!this.editAction) {
        this.$router.push({ name: "service-hours" });
      } else {
        this.readOnly = true;
      }
    },
    redirectBack() {
      this.$router.push({ name: "service-hours" });
    },

    setDefaultValue(key) {
      if (this.customServiceHours[key].active == true) {
        this.customServiceHours[key].start_time = "09:00 AM";
        this.customServiceHours[key].end_time = "05:00 PM";
        this.changeEndTime(
          key,
          this.customServiceHours[key].start_time,
          this.customServiceHours[key].end_time
        );
      } else {
        this.customServiceHours[key].start_time = "";
        this.customServiceHours[key].end_time = "";
      }
    },

    validateHours() {
      let valid = true;
      if (this.customHours == "custom") {
        for (const item in this.customServiceHours) {
          if (this.customServiceHours[item].error == true) {
            valid = false;
          }
        }
      }
      return valid;
    },

    async createServiceHour() {
      if (this.formValid && this.validateHours()) {
        let payload = this.validFormatedData();
        if (
          this.customHours == "custom" &&
          Object.keys(this.timings).length == 0
        ) {
          this.showError = true;
          setTimeout(() => {
            this.showError = false;
          }, 4000);
        } else {
          this.loading = true;
          try {
            const response = await $servicehours.create(payload, {});
            if (response.data) {
              this.$router.push({ name: "service-hours" });
              this.$store.dispatch("ui/snackbar", [
                {
                  title: "",
                  message: `A new Service hour has been created`,
                  type: "success",
                },
              ]);
              this.loading = false;
            }
          } catch (error) {
            this.loading = false;
            if (error.response !== undefined) {
              this.$store.dispatch("ui/snackbar", [
                {
                  title: "Error",
                  message: this.$errors(error.response.data).msg({
                    capitalize: false,
                    prepend: `${this.$errors(
                      error.response.data
                    ).subjectText()} `,
                  }),
                  type: "error",
                },
              ]);
            }
          }
        }
      }
    },

    validFormatedData() {
      if (this.customHours == "custom") {
        for (const item in this.customServiceHours) {
          if (this.customServiceHours[item].active) {
            let day = {
              start_time: this.customServiceHours[item].start_time,
              end_time: this.customServiceHours[item].end_time,
            };
            this.timings[item] = day;
          }
        }
      }
      let formatedHolidays = {};
      this.holidayList.forEach((item) => {
        formatedHolidays[item.date] = item.name;
      });
      let payload = {
        service_hour: {
          name: this.name,
          timezone: this.selectedTimeZone,
          description: this.description,
          custom: this.customHours == "custom" ? true : false,
          timings: this.timings,
          holidays: formatedHolidays,
        },
      };
      return payload;
    },

    async editServiceHours() {
      if (this.formValid && this.validateHours()) {
        let payload = this.validFormatedData();
        if (
          this.customHours == "custom" &&
          Object.keys(this.timings).length == 0
        ) {
          this.showError = true;
          setTimeout(() => {
            this.showError = false;
          }, 4000);
        } else {
          this.loading = true;
          let id = this.$route.params.id;
          try {
            const response = await $servicehours.update(id, payload, {});
            if (response.data) {
              this.$store.dispatch("ui/snackbar", [
                {
                  title: "",
                  message: `Service Hours has been updated.`,
                  type: "success",
                },
              ]);
              this.readOnly = true;
              this.loading = false;
            }
          } catch (error) {
            this.loading = false;
            if (error.response !== undefined) {
              this.$store.dispatch("ui/snackbar", [
                {
                  title: "Error",
                  message: this.$errors(error.response.data).msg({
                    capitalize: false,
                    prepend: `${this.$errors(
                      error.response.data
                    ).subjectText()} `,
                  }),
                  type: "error",
                },
              ]);
            }
          }
        }
      }
    },

    /* eslint-disable */
    async getServiceHour(hourId) {
      this.loading = true;
      try {
        const response = await $servicehours.singleHours(hourId, {});
        if (response.data) {
          this.loading = false;
          this.setFieldsData(response.data);
        }
      } catch (error) {
        this.loading = false;
      }
    },

    setFieldsData(data) {
      this.name = data.name;
      if (this.$route.query.clone == "true") {
        this.name = "Copy of " + data.name;
        this.readOnly = false;
      }
      this.pageHeading = data.name;
      this.breadcrumbsList[2].text = this.pageHeading;
      this.description = data.description;
      this.selectedTimeZone = data.timezone;
      this.associatedGroups = data.group_names;
      if (data.custom == true) {
        this.customHours = "custom";
        for (const item in data.timings) {
          this.customServiceHours[item].active = true;
          this.customServiceHours[item].start_time =
            data.timings[item].start_time;
          this.customServiceHours[item].end_time = data.timings[item].end_time;
          this.changeEndTime(
            item,
            this.customServiceHours[item].start_time,
            this.customServiceHours[item].end_time
          );
        }
      }
      if (data.custom == false) {
        this.customHours = "24hours";
      }

      if (Object.keys(data.holidays).length) {
        for (const item in data.holidays) {
          let holiday = {};
          holiday.name = data.holidays[item];
          holiday.date = item;
          holiday.id = data.holidays[item] + item;
          this.holidayList.push(holiday);
        }
      }
    },

    /** custom service hours validation */
    timeList() {
      const startDate = new Date(1970, 0, 1);
      let timeSlots = [];
      while (startDate.getDate() === 1) {
        timeSlots.push(
          startDate.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
          })
        );
        startDate.setMinutes(startDate.getMinutes() + 15);
      }
      return timeSlots;
    },

    changeEndTime(day, strStartTime, strEndTime) {
      if (strStartTime) {
        Object.keys(this.customServiceHours).forEach((day) => {
          if (this.customServiceHours[day].start_time == strStartTime) {
            let hoursList = this.timeList();
            let filteredData = hoursList.filter((time) => {
              return (
                this.convert24Hours(time) > this.convert24Hours(strStartTime)
              );
            });
            this.customServiceHours[day].endHours = filteredData;
          }
        });
        this.checkTimeValidation(day, strStartTime, strEndTime);
      }
    },

    displayError(selectedDay, showError) {
      for (const item in this.customServiceHours) {
        if (item == selectedDay && showError == true) {
          this.customServiceHours[selectedDay].error = true;
        }
        if (item == selectedDay && showError == false) {
          this.customServiceHours[selectedDay].error = false;
        }
      }
    },
    /** Start time and end time validation */
    checkTimeValidation(day, strStartTime, strEndTime) {
      if (strStartTime && strEndTime) {
        if (
          this.convert24Hours(strStartTime) >= this.convert24Hours(strEndTime)
        ) {
          this.startEndTimeError = true;
          this.displayError(day, true);
          this.validateHours();
        } else {
          this.displayError(day, false);
        }
      }
    },

    convert24Hours(timeStr) {
      const [time, modifier] = timeStr.split(" ");
      let [hours, minutes] = time.split(":");
      if (hours === "12") {
        hours = "00";
      }
      if (modifier === "PM") {
        hours = parseInt(hours, 10) + 12;
      }
      return `${hours}:${minutes}`;
    },
    getMinutes(d) {
      return parseInt(d.split(":")[1].split(" ")[0]);
    },
  },

  /*** */
};
</script>

<style lang="scss">
.setservice-hours,
.holidays-section {
  min-height: 715px;
}
</style>
